import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import FalloPantalla from './FalloPantalla';
import PantallaLogin from './PantallaLogin';
import { useAuth } from '@/hooks/useApi';
import { UsuarioContext } from '@/hooks/useUsuario';
import { useTheme } from '@/hooks/useTheme';
import { useSesionCaducada, useVigilarSesion } from '@/lib/sesion';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import AnclaCCP from '@/components/centralita/AnclaCCP';
import BarraLlamada from '@/components/centralita/BarraLlamada';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, loading, error } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const caducada = useSesionCaducada();
  useVigilarSesion(user !== null);

  // Con la sesión caducada el CRM se queda montado debajo de la pantalla de
  // login —para no perder lo escrito ni cortar una llamada—, pero sin que se
  // pueda tocar: `inert` corta también el teclado, que la capa no tapa.
  const crm = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (crm.current) crm.current.inert = caducada;
  }, [caducada, user]);

  // Wait for auth check
  if (loading) {
    return (
      <div className="min-h-screen bg-sereno-white flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return error
      ? <PantallaLogin motivo="sin-conexion" detalle={error} />
      : <PantallaLogin motivo="entrar" />;
  }

  return (
    <UsuarioContext.Provider value={user}>
      <div ref={crm} className="min-h-screen bg-sereno-white">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} theme={theme} onToggleTheme={toggleTheme} />
        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-sereno-dark flex items-center px-4 z-20">
          <button onClick={() => setSidebarOpen(true)} className="text-white p-1" aria-label="Abrir menú">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          </button>
          <img src="https://serenodocsstor.blob.core.windows.net/assets/sereno-neg.png" alt="Sereno" className="h-7 ml-3" />
        </div>
        <main className="lg:ml-56 min-w-0 p-4 pt-16 lg:p-6 lg:pt-6">
          <FalloPantalla ruta={pathname}>{children}</FalloPantalla>
        </main>
        {/* Van aquí, fuera de <Routes>, a propósito: el iframe del CCP sostiene la
            conexión de voz, y desmontarlo al navegar cortaría la llamada. Este
            layout no se desmonta nunca mientras haya sesión, y tampoco cuando
            una pantalla falla: FalloPantalla se queda el error antes de que suba. */}
        <AnclaCCP />
        <BarraLlamada />
      </div>
      {caducada && <PantallaLogin motivo="caducada" />}
    </UsuarioContext.Provider>
  );
}
